/* eslint-disable camelcase */
import { createListingPageUrlFromAttributes } from '../listingPageUrlParser/listingPageUrlParser';
import { getPageUrlPrefixForSegmentListingPage } from './helpers/converterHelper';

export function convertFiltersFromSearchLabsToReduxFormat(
  searchLabsAPIData, pageUrlStart, pageAttributes, ucmsCategorization) {

  const searchLabsFiltersFromResponse = searchLabsAPIData.facets ? searchLabsAPIData.facets : [];

  // getting selectedSingleBrandFilterLabel
  let selectedSingleBrandFilterLabel;
  const selectedValuesOfBrandFilters =
    searchLabsFiltersFromResponse.find((filter) => filter.id === 'brand_id')
      ? searchLabsFiltersFromResponse.find((filter) => filter.id === 'brand_id')
        .values
        .filter((value) => value.selected)
      : undefined;

  // getting selectedSingleBrandFilterLabel
  if (selectedValuesOfBrandFilters && selectedValuesOfBrandFilters.length === 1) {
    selectedSingleBrandFilterLabel = selectedValuesOfBrandFilters[0].label;
  }

  let selectedSingleTypeFilterLabel;
  const selectedValuesOfTypeFilters =
    searchLabsFiltersFromResponse.find((filter) => filter.label === 'Type')
      ? searchLabsFiltersFromResponse.find((filter) => filter.label === 'Type')
        .values
        .filter((value) => value.selected)
      : undefined;

  if (selectedValuesOfTypeFilters && selectedValuesOfTypeFilters.length === 1) {
    selectedSingleTypeFilterLabel = selectedValuesOfTypeFilters[0].label;
  }

  // cloning filters in order not to change the data from response
  const searchLabsFilters = searchLabsFiltersFromResponse.map(
    (filter) => ({ ...filter,
      // slice is used here for cloning filter.values because we don't want change original filter.values array
      values: filter.values.slice(0)
    })
  );

  const normalizedFiltersFromSearchLabs = [];

  // the Segment filter has to be always ordered as first if present in the response
  const segmentIdFilterItemIndex = searchLabsFilters.findIndex((filterItem) => filterItem.id === 'segment_id');
  if (segmentIdFilterItemIndex !== -1) {
    const segmentIdFilterItem = searchLabsFilters.splice(segmentIdFilterItemIndex, 1)[0];

    // the segment filter is only added to normalize data if it contain 2 or more values
    // because there is nothing to filter in segment filters if there is just one segment
    if (segmentIdFilterItem.values.length > 1) {
      // the segment filter is always unfolded by default in UI
      segmentIdFilterItem.openedByDefault = true;
      normalizedFiltersFromSearchLabs.push(segmentIdFilterItem);
    }
  }

  // the Brand filter has to be always ordered as second or less if present in the response
  const brandIdFilterItemIndex = searchLabsFilters.findIndex((filterItem) => filterItem.id === 'brand_id');
  if (brandIdFilterItemIndex !== -1) {
    const brandIdFilterItem = searchLabsFilters.splice(brandIdFilterItemIndex, 1)[0];

    // this removes brand filter values which are selected but are not in pageAttributes.filterAttributes.brand_id
    // These selected filters which are not in pageAttributes.filterAttributes.brand_id are added by searchLabs algorithm
    // and selecting/deselecting them doesn't change number of products on the page
    brandIdFilterItem.values = brandIdFilterItem.values.filter((brandFilterValue) =>
      !brandFilterValue.selected || pageAttributes?.filterAttributes?.brand_id?.includes(brandFilterValue.value)
    );

    // the brand filter is only added to normalize data if it contain 1 or more values
    // because there is nothing to filter if there are no values
    if (brandIdFilterItem.values.length > 0) {
      brandIdFilterItem.label = 'Brand'; // TODO: Wrong label is coming from searchLabs, so we need to hardcode the labels here. Tell serachlabs to correct it or add it to translations

      // the Brand filter is always unfolded by default in UI
      brandIdFilterItem.openedByDefault = true;
      normalizedFiltersFromSearchLabs.push(brandIdFilterItem);
    }
  }

  // the Customer Rating filter has to be always ordered as third or less if present in the response
  const customerRatingFilterItemIndex = searchLabsFilters.findIndex((filterItem) => filterItem.id === 'rounded_score');
  if (customerRatingFilterItemIndex !== -1) {
    const customerRatingFilterItem = searchLabsFilters.splice(customerRatingFilterItemIndex, 1)[0];
    customerRatingFilterItem.label = 'Customer rating'; // TODO: Wrong label is coming from searchLabs, so we need to hardcode the labels here. Tell serachlabs to correct it or add it to translations
    // values of Customer Rating filter are special and they need to be normalized
    // it normalizes data and also creates 'linkToListingPage' property in case of segment listing page
    customerRatingFilterItem.values = normalizeCustomerRatingFilterItemValues(
      customerRatingFilterItem.values,
      // pageUrlPrefix is only required for segment listing page and not for search page
      ucmsCategorization
        ? getPageUrlPrefixForSegmentListingPage(
          pageUrlStart, selectedSingleBrandFilterLabel,
          selectedSingleTypeFilterLabel, ucmsCategorization
        )
        : null,
      pageAttributes,
      ucmsCategorization
    );

    // the Customer Rating filter is always unfolded by default in UI
    customerRatingFilterItem.openedByDefault = true;
    normalizedFiltersFromSearchLabs.push(customerRatingFilterItem);
  }

  // the Delivery filter has to be always ordered as fourth or less if present in the response
  const deliveryFilterItemIndex = searchLabsFilters.findIndex((filterItem) => filterItem.id === 'delivery');
  if (deliveryFilterItemIndex !== -1) {
    const deliveryFilterItem = searchLabsFilters.splice(deliveryFilterItemIndex, 1)[0];
    deliveryFilterItem.label = 'Getting it to you'; // TODO: Wrong label is coming from searchLabs, so we need to hardcode the labels here. Tell serachlabs to correct it or add it to translations
    // values of Delivery filter are special and they need to be normalized
    deliveryFilterItem.values = normalizeDeliveryFilterItemValues(deliveryFilterItem.values);

    // the Delivery filter is always unfolded by default in UI
    deliveryFilterItem.openedByDefault = true;
    normalizedFiltersFromSearchLabs.push(deliveryFilterItem);
  }

  // the Type filter has to be always ordered as fifth or less if present in the response
  const typeFilterItemIndex = searchLabsFilters.findIndex((filterItem) => filterItem.label === 'Type'); // TODO: change detecting type filter to a one not based on label which can change in future
  if (typeFilterItemIndex !== -1) {
    const typeFilterItem = searchLabsFilters.splice(typeFilterItemIndex, 1)[0];

    // the Type filter is always unfolded by default in UI
    typeFilterItem.openedByDefault = true;
    normalizedFiltersFromSearchLabs.push(typeFilterItem);
  }

  // copying the renaming filter items from response to normalized filters
  searchLabsFilters.forEach((filterItem) => {
    normalizedFiltersFromSearchLabs.push(filterItem);
  });


  // this branch generates links to listing page for brand filters and type filters values for segment listing page
  // this links are required for SEO purposes (google crawler etc.)
  //
  // so when users activates/deactivates (depends if filter is selected or deselected) this filter,
  // the 'linkToListingPage' value represents URL of listing page after activating/deactivating
  //
  // if ucmsCategorization is not defined it means that it's search page and we don't define
  // linkToListingPage for search listing page
  if (ucmsCategorization) {
    // checks if we can add links to brand filters values and type filter values
    const [addLinkToBrandFilterValuePossible, addLinkToTypeFilterValuePossible] =
      isAddingLinkToBrandAndTypeFilterValuePossible(pageAttributes, normalizedFiltersFromSearchLabs);
    
    if (addLinkToBrandFilterValuePossible) {
      // we can add links to brand filter values
      const brandFiltersObject =
        normalizedFiltersFromSearchLabs.find((filterFromSearchLabs) => filterFromSearchLabs.id === 'brand_id');

      if (brandFiltersObject && Array.isArray(brandFiltersObject.values)) {
        brandFiltersObject.values = brandFiltersObject.values.map((filterValueItem, index, values) => {
          const listingPageUrlPrefix = createLinkPrefixToListingPageForBrandFilterValueItem(pageUrlStart,
            values, filterValueItem, selectedSingleTypeFilterLabel, ucmsCategorization);

          const listingPageUrlSuffix = createLinkSuffixToListingPageForFilterValueItem(
            filterValueItem, 'brand_id', pageAttributes
          );

          return {
            ...filterValueItem,
            linkToListingPage: listingPageUrlPrefix + listingPageUrlSuffix
          };
        });
      }
    }
    
    if (addLinkToTypeFilterValuePossible) {
      // we can add links to type filter values
      const typeFiltersObject = normalizedFiltersFromSearchLabs
        .find((filterFromSearchLabs) => filterFromSearchLabs.label === 'Type');

      if (typeFiltersObject && Array.isArray(typeFiltersObject.values)) {
        typeFiltersObject.values = typeFiltersObject.values.map((filterValueItem, index, values) => {
          const listingPageUrlPrefix = createLinkPrefixToListingPageForTypeFilterValueItem(pageUrlStart,
            values, filterValueItem, selectedSingleBrandFilterLabel, ucmsCategorization);

          const listingPageUrlSuffix = createLinkSuffixToListingPageForFilterValueItem(
            filterValueItem, typeFiltersObject.id, pageAttributes
          );

          return {
            ...filterValueItem,
            linkToListingPage: listingPageUrlPrefix + listingPageUrlSuffix
          };
        });
      }


    }
  }
  return normalizedFiltersFromSearchLabs;
}


// checks if it is possible to add links to brand and type filter values based on page attributes ans selected filters
// @returns [addLinkToBrandFilterValuePossible {bool}, addLinkToTypeFilterValuePossible {bool}]
function isAddingLinkToBrandAndTypeFilterValuePossible(pageAttributes, normalizedFiltersFromSearchLabs) {
  if (pageAttributes.page !== '1' || pageAttributes.m_results_per_page !== '20' || pageAttributes.sort !== 'relevance-desc') {
    // Links can be added only for brand or type filters with first page, 20 products per page and default sorting
    // which is not fulfilled in this case so we return [false, false]
    return [false, false];
  }


  if (typeof pageAttributes.price_min !== 'undefined') {
    // Links can be added only if no price filter is selected
    // which is not fulfilled in this case so we return [false, false]
    return [false, false];
  }

  let numberOfSelectedFiltersWithoutSelectedBrandAndTypeFilters = 0;
  let numberOfSelectedBrandFilters = 0;
  let numberOfSelectedTypeFilters = 0;

  normalizedFiltersFromSearchLabs.forEach((filterObject) => {
    if (filterObject.id === 'brand_id') {
      filterObject.values.forEach((value) => {
        if (value.selected) {
          numberOfSelectedBrandFilters++;
        }
      });

    } else if (filterObject.label === 'Type') {
      filterObject.values.forEach((value) => {
        if (value.selected) {
          numberOfSelectedTypeFilters++;
        }
      });
    } else {
      filterObject.values.forEach((value) => {
        if (value.selected) {
          numberOfSelectedFiltersWithoutSelectedBrandAndTypeFilters++;
        }
      });
    }
  });

  if (numberOfSelectedFiltersWithoutSelectedBrandAndTypeFilters !== 0 || numberOfSelectedBrandFilters > 1 ||
    numberOfSelectedTypeFilters > 1) {
    // Links can be added if no other filter value (excepting type and brand filter is selected)
    // AND number of selected brand filters is 0 or 1
    // AND number of selected type filters is 0 or 1
    // which is not fulfilled in this case so we return [false, false]
    return [false, false];
  }

  if (numberOfSelectedBrandFilters === 0 && numberOfSelectedTypeFilters === 1) {
    // link to brand filters can be added
    // when one type filter value is selected
    return [true, false];
  }

  if (numberOfSelectedBrandFilters === 1 && numberOfSelectedTypeFilters === 0) {
    // link to type filters can be added
    // when one brand filter value is selected
    return [false, true];
  }


  if (numberOfSelectedBrandFilters === 0 && numberOfSelectedTypeFilters === 0) {
    // link to both brand and type filters can be added
    // when no filter is selected
    return [true, true];
  }

  return [false, false];
}

function normalizeCustomerRatingFilterItemValues(values) {
  const normalizedValues = [];

  const valueItem10 = values.find((valueItem) => valueItem.value === '10');
  const valueItem8 = values.find((valueItem) => valueItem.value === '8');
  const valueItem6 = values.find((valueItem) => valueItem.value === '6');

  // value for 10 (5 stars)
  if (valueItem10) {
    const isValueItem10Selected =
      valueItem10.selected && (valueItem6 ? !valueItem6.selected : true) && (valueItem8 ? !valueItem8.selected : true);

    normalizedValues.push({
      value: '10',
      label: '5', // TODO: add to translations
      labelMobile: '5 stars', // TODO: add to translations
      count: String(valueItem10.count),
      selected: isValueItem10Selected
    });
  }

  // value for 8+ (4 stars and more)
  if (valueItem8) {
    const isValueItem8Selected =
      valueItem8.selected && (valueItem6 ? !valueItem6.selected : true);

    normalizedValues.push({
      value: '8,10',
      label: '4 or more', // TODO: add to translations
      labelMobile: '4 stars or more', // TODO: add to translations
      count: String((valueItem8 ? Number(valueItem8.count) : 0) + (valueItem10 ? Number(valueItem10.count) : 0)),
      selected: isValueItem8Selected
    });
  }

  // value for 6+ (3 stars and more)
  if (valueItem6) {
    const isValueItem6Selected = valueItem6.selected;

    normalizedValues.push({
      value: '6,8,10',
      label: '3 or more', // TODO: add to translations
      labelMobile: '3 stars or more', // TODO: add to translations
      count: String((valueItem6 ? Number(valueItem6.count) : 0) + (valueItem8 ? Number(valueItem8.count) : 0) +
        (valueItem10 ? Number(valueItem10.count) : 0)),
      selected: isValueItem6Selected
    });
  }

  return normalizedValues;
}

function normalizeDeliveryFilterItemValues(values) {
  return values
    .filter(
      (valueItem) => valueItem.value === 'hd' ||
        valueItem.value === 'storereservation' ||
        valueItem.value === 'pnc' ||
        valueItem.value === 'downloads'
    )
    .map((valueItem) => {
      switch (valueItem.value) {
        case 'hd': {
          return {
            ...valueItem,
            label: 'To deliver' // TODO: add to translations
          };
        }
        case 'storereservation': {
          return {
            ...valueItem,
            label: 'To collect within an hour' // TODO: add to translations
          };
        }
        case 'pnc': {
          return {
            ...valueItem,
            label: 'To collect from next day' // TODO: add to translations
          };
        }
        case 'downloads': {
          return {
            ...valueItem,
            label: 'To download' // TODO: add to translations
          };
        }
        default: {
          return null;
        }
      }
    });
}

function createLinkPrefixToListingPageForBrandFilterValueItem(
  pageUrlStart, brandFiltersFromSearchLabsValuesItems, brandFilterValueItemForUrl,
  selectedSingleTypeFilterLabel, ucmsCategorization) {
  const currenlySelectedBrandFilterValuesItems = brandFiltersFromSearchLabsValuesItems.filter(
    (filterValueItem) => filterValueItem.selected);

  if (brandFilterValueItemForUrl.selected && currenlySelectedBrandFilterValuesItems.length === 2) {
    // two brand filters values are activates so by deactivating one of them prefix should contain label of the
    // the active brand filter
    return getPageUrlPrefixForSegmentListingPage(
      pageUrlStart,
      currenlySelectedBrandFilterValuesItems.find((filterValueItem) => filterValueItem !== brandFilterValueItemForUrl).label,
      selectedSingleTypeFilterLabel,
      ucmsCategorization
    );
  }

  // no brand filter is activated, so afterwards if any brand filter value is activated, the url prefix have to contain
  // the name of the brand
  if (currenlySelectedBrandFilterValuesItems.length === 0) {
    return getPageUrlPrefixForSegmentListingPage(
      pageUrlStart,
      brandFilterValueItemForUrl.label,
      selectedSingleTypeFilterLabel,
      ucmsCategorization
    );
  }

  // all the other options
  return getPageUrlPrefixForSegmentListingPage(
    pageUrlStart,
    undefined,
    selectedSingleTypeFilterLabel,
    ucmsCategorization
  );
}

function createLinkPrefixToListingPageForTypeFilterValueItem(
  pageUrlStart, typeFiltersFromSearchLabsValuesItems, typeFilterValueItemForUrl,
  selectedSingleBrandFilterLabel, ucmsCategorization) {
  const currenlySelectedTypeFilterValuesItems = typeFiltersFromSearchLabsValuesItems.filter(
    (filterValueItem) => filterValueItem.selected);

  if (typeFilterValueItemForUrl.selected && currenlySelectedTypeFilterValuesItems.length === 2) {

    // two Type filters values are activates so by deactivating one of them prefix should contain label of the
    // the active Type filter
    return getPageUrlPrefixForSegmentListingPage(
      pageUrlStart,
      selectedSingleBrandFilterLabel,
      currenlySelectedTypeFilterValuesItems.find((filterValueItem) => filterValueItem !== typeFilterValueItemForUrl).label,
      ucmsCategorization
    );
  }

  // no Type filter is activated, so afterwards if any Type filter value is activated, the url prefix have to contain
  // the name of the Type
  if (currenlySelectedTypeFilterValuesItems.length === 0) {
    return getPageUrlPrefixForSegmentListingPage(
      pageUrlStart,
      selectedSingleBrandFilterLabel,
      typeFilterValueItemForUrl.label,
      ucmsCategorization
    );
  }

  // all the other options
  return getPageUrlPrefixForSegmentListingPage(
    pageUrlStart,
    selectedSingleBrandFilterLabel,
    undefined,
    ucmsCategorization
  );
}

function createLinkSuffixToListingPageForFilterValueItem(filterValueItem, filterId, pageAttributes) {
  if (filterValueItem.selected) {
    return createListingPageUrlFromAttributes({
      ...pageAttributes,
      filterAttributes: {
        ...pageAttributes.filterAttributes,
        [filterId]: pageAttributes.filterAttributes[filterId].length > 1 // the pageAttributes.filterAttributes[filterId] array has to exist because filter is selected for this value
          ? pageAttributes.filterAttributes[filterId].filter((value) => value !== filterValueItem.value)
          : undefined // if length of array is 1, it means there's only that element in array which we want to remove so we return undefined
      }
    });
  }

  // when filter is not selected
  return createListingPageUrlFromAttributes({
    ...pageAttributes,
    filterAttributes: {
      ...pageAttributes.filterAttributes,
      [filterId]: Array.isArray(pageAttributes.filterAttributes[filterId]) // the pageAttributes.filterAttributes[filterId] array has to exist because filter is selected for this value
        ? [...pageAttributes.filterAttributes[filterId]].concat(filterValueItem.value) // we need to clone pageAttributes.filterAttributes[filterId] because we don't want to change its value
        : [filterValueItem.value]
    }
  });
}
